<template>
	<li
		class="file-overview-list-item drop-shadow-xs"
		:class="selected ? 'active' : ''"
		@click.shift.exact="fileClicked('between')"
		@click.shift.alt.exact="fileClicked('between')"
		@click.shift.meta.exact="fileClicked('between')"
		@click.shift.alt.meta.exact="fileClicked('between')"
		@click.ctrl.exact="fileClicked('add')"
		@click.ctrl.alt.exact="fileClicked('add')"
		@click.ctrl.meta.exact="fileClicked('add')"
		@click.ctrl.alt.meta.exact="fileClicked('add')"
		@click.exact="fileClicked('replace')"
		@click.alt.exact="fileClicked('replace')"
		@click.meta.exact="fileClicked('replace')"
		@click.alt.meta.exact="fileClicked('replace')"
	>
		<img class="img" :src="fileIcon" draggable="false" />
		<span>
			{{ props.file.filename }}
		</span>
		<span class="upload-date">{{ uploadDateLabel }}</span>
		<span
			v-if="classification"
			class="classification-badge"
			:class="
				classification.confidence >= CLASSIFICATIE_HOGE_CONFIDENCE
					? 'high-confidence'
					: classification.confidence >= CLASSIFICATIE_MIN_CONFIDENCE
						? 'medium-confidence'
						: 'unknown-confidence'
			"
			:title="`${Math.round(classification.confidence * 100)}% zekerheid`"
		>
			{{
				classification.confidence >= CLASSIFICATIE_MIN_CONFIDENCE
					? classification.label
					: CLASSIFICATIE_ONBEKEND_LABEL
			}}
		</span>
		<span v-if="pendingBadgeText" class="pending-classification">
			{{ pendingBadgeText }}
		</span>
	</li>
</template>

<script lang="ts" setup>
import { getFileIcon, getFileType } from "@/helpers/fileHelpers";
import { useNow } from "@vueuse/core";
import { DateTime } from "luxon";
import { computed, ref } from "vue";
import type { FileClickMode } from "@/enums/FileClickMode";
import type { DossierDocument } from "@/composables/useLoanApplicationSortedDocuments";
import {
	CLASSIFICATIE_MIN_CONFIDENCE,
	CLASSIFICATIE_HOGE_CONFIDENCE,
	CLASSIFICATIE_ONBEKEND_LABEL,
} from "@/parameters/documentClassificatie";
import { getPendingClassificationStatus } from "@/lib/documentClassificatieStatus";

const props = defineProps<{
	file: DossierDocument;
	classification?: { label: string; confidence: number } | null;
	selected?: boolean;
	classificationFeatureEnabled?: boolean;
}>();

const emit =
	defineEmits<
		(e: "click", doubleClick: boolean, clickMode: FileClickMode) => void
	>();

const fileIcon = computed(() => getFileIcon(getFileType(props.file)));

const uploadDateLabel = computed(() => {
	const timestamp = props.file.uploadedAt;
	if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) {
		return "Uploaddatum onbekend";
	}
	const date = DateTime.fromMillis(timestamp);
	return date.isValid
		? `Geüpload op ${date.setLocale("nl-NL").toFormat("dd-MM-yyyy")}`
		: "Uploaddatum onbekend";
});

// Reactieve tijdbron zodat de pending-computed herberekent na PENDING_WINDOW_MS
// (1 min interval is ruim genoeg voor de 5-min-grens).
const now = useNow({ interval: 60_000 });

const pendingStatus = computed(() => {
	if (props.classification) return null;
	return getPendingClassificationStatus(props.file, {
		featureFlagEnabled: props.classificationFeatureEnabled === true,
		now: now.value.getTime(),
	});
});

const pendingBadgeText = computed(() => {
	switch (pendingStatus.value) {
		case "pending":
			return "Wordt geclassificeerd";
		case "not-classifiable":
			return "Niet classificeerbaar";
		default:
			return null;
	}
});

const doubleClickTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

// FIXME: Calling fileClicked based on the modifier is done in a hacky way.
//        We want to use the 'exact' modifier to not trigger the regular click when shift or ctrl is pressed.
//        We also want the click to still fire with alt or meta pressed.
//        A better solution would be a 'not' modifier, where we only fire the click when shift and ctrl are not pressed.
//        Since this doesn't exist, we explicitly define the cases with alt and or meta pressed.
const fileClicked = (mode: FileClickMode) => {
	if (!doubleClickTimeout.value) {
		emit("click", false, mode);
		doubleClickTimeout.value = setTimeout(() => {
			doubleClickTimeout.value = null;
		}, 500);
	} else {
		clearTimeout(doubleClickTimeout.value);
		doubleClickTimeout.value = null;
		emit("click", true, mode);
	}
};
</script>

<style lang="scss">
.file-overview-list-item {
	list-style-type: none;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 20px;
	border-radius: var(--border-radius);
	outline: 1px solid var(--border);
	row-gap: 10px;
	cursor: pointer;
	user-select: none;
	background: white;

	@media screen and (max-width: 600px) {
		padding: 10px;
	}

	&:hover {
		background: var(--background-primary-hover);
	}

	&.active {
		outline: 2px solid var(--accent-primary);
	}

	span {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
		word-wrap: break-word;
		word-break: break-word;

		max-width: 100%;
		text-align: center;
		color: var(--text-secondary);
		font-size: 0.9rem;
	}

	.upload-date {
		display: block;
		-webkit-line-clamp: unset;
		line-clamp: unset;
		overflow: visible;
		font-size: 0.75rem;
	}

	.pending-classification {
		display: inline-block;
		padding: 2px 8px;
		border-radius: 4px;
		font-size: 0.75rem;
		line-clamp: 1;
		-webkit-line-clamp: 1;
		background: var(--background-secondary);
		color: var(--text-secondary);
		font-style: italic;
	}

	.classification-badge {
		display: inline-block;
		padding: 2px 8px;
		border-radius: 4px;
		font-size: 0.75rem;
		line-clamp: 1;
		-webkit-line-clamp: 1;

		&.high-confidence {
			background: color-mix(in srgb, var(--color-success) 15%, white);
			color: var(--color-success);
		}

		&.medium-confidence {
			background: var(--background-secondary);
			color: var(--text-secondary);
		}

		&.unknown-confidence {
			background: color-mix(in srgb, var(--color-warn) 15%, white);
			color: var(--color-warn);
			font-style: italic;
		}
	}
}
</style>
